import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Search, Bell, User, Settings, LogOut, ChevronDown, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../../services/auth';
import { notificacoesService, Notificacao } from '../../services/notificacoes';

import { formatRelativeTime } from '../../utils/formatters';
import { Paginacao } from '../../types';


/**
 * Para onde cada tipo de aviso leva ao ser clicado no sininho.
 *
 * Espelha `rota` do catalogo backend/config/notificacoes.php, que ja define o
 * mesmo destino para o botao do e-mail. Sao dois lugares para atualizar quando
 * surgir um tipo novo; o dia em que isso incomodar, a rota deve vir no proprio
 * payload da notificacao em vez de ser mapeada aqui.
 */
const ROTA_POR_TIPO: Record<string, string> = {
  motorista_pendente: '/aprovacoes',
  motorista_aprovado: '/motoristas',
  motorista_rejeitado: '/motoristas',
  abastecimento: '/abastecimentos',
  manutencao: '/manutencoes',
  despesa: '/despesas',
  viagem: '/viagens',
};

/** Tipo sem destino conhecido cai no painel, nunca num link quebrado. */
const destinoDe = (tipo?: string): string => ROTA_POR_TIPO[tipo ?? ''] ?? '/dashboard';

interface HeaderProps {
  onMenuToggle: () => void;
}

// Reconsulta o contador em segundo plano, para o número no sininho não ficar
// parado numa sessão longa até a pessoa abrir o dropdown por algum outro motivo.
const INTERVALO_ATUALIZACAO_MS = 60_000;

// Tamanho da página pedido ao servidor tanto na abertura do dropdown quanto no
// "Carregar mais": manter o mesmo valor evita saltos/repetições na rolagem.
const NOTIFICACOES_POR_PAGINA = 20;

const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [naoLidas, setNaoLidas] = useState(0);
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [carregandoNotificacoes, setCarregandoNotificacoes] = useState(false);
  const [paginacaoNotificacoes, setPaginacaoNotificacoes] = useState<Paginacao | null>(null);
  const [carregandoMais, setCarregandoMais] = useState(false);

  const authUser = authService.getUser();
  const user = {
    name: authUser?.name || 'Usuario',
    email: authUser?.email || '',
    avatar: null,
    role: authUser?.role === 'admin' ? 'Administrador' : authUser?.role === 'gestor' ? 'Gestor' : 'Operador',
    empresa: authUser?.empresa?.nome || '',
    logo: authUser?.empresa?.logo || null,
  };

  const atualizarContador = useCallback(async () => {
    try {
      const res = await notificacoesService.naoLidas();
      setNaoLidas(res.data.count);
    } catch {
      // Falha silenciosa: o sininho apenas mantém o último número conhecido.
      // Um erro aqui não pode virar toast a cada minuto de sessão aberta.
    }
  }, []);

  useEffect(() => {
    atualizarContador();
    const intervalo = window.setInterval(atualizarContador, INTERVALO_ATUALIZACAO_MS);
    return () => window.clearInterval(intervalo);
  }, [atualizarContador]);

  // A lista completa só é buscada quando o dropdown abre: evita uma consulta
  // extra a cada carregamento de página só para preencher um painel fechado.
  // Toda abertura recomeça da página 1 e SUBSTITUI a lista: o que foi acumulado
  // com "Carregar mais" numa abertura anterior não sobrevive ao fechamento.
  useEffect(() => {
    if (!isNotificationsOpen) return;
    setCarregandoNotificacoes(true);
    notificacoesService.listar(1, NOTIFICACOES_POR_PAGINA, true)
      .then((res) => {
        setNotificacoes(res.data);
        setPaginacaoNotificacoes(res.pagination ?? null);
      })
      .catch(() => {
        setNotificacoes([]);
        setPaginacaoNotificacoes(null);
      })
      .finally(() => setCarregandoNotificacoes(false));
  }, [isNotificationsOpen]);

  const carregarMaisNotificacoes = async () => {
    if (!paginacaoNotificacoes) return;
    setCarregandoMais(true);
    try {
      const res = await notificacoesService.listar(
        paginacaoNotificacoes.current_page + 1,
        NOTIFICACOES_POR_PAGINA,
        true,
      );
      setNotificacoes((atual) => [...atual, ...res.data]);
      setPaginacaoNotificacoes(res.pagination ?? null);
    } catch {
      // A página só avança no sucesso, então o clique continua repetível: uma
      // falha de rede não pode fazer a lista "pular" um bloco de notificações.
      toast.error('Não foi possível carregar mais notificações.');
    } finally {
      setCarregandoMais(false);
    }
  };

  /**
   * Clicar num aviso resolve o aviso: leva para a tela onde ele e tratado e o
   * tira da lista.
   *
   * Antes o clique so pintava o item como lido e ele continuava ali. O gestor
   * precisava sair do sininho e procurar a tela na mao, e a lista virava um
   * acumulado de avisos ja resolvidos que escondia os novos.
   *
   * A remocao e local: no servidor a notificacao segue existindo, marcada como
   * lida, e continua no historico. O sininho e a fila do que falta ver.
   */
  const abrirNotificacao = async (notificacao: Notificacao) => {
    setNotificacoes((atual) => atual.filter((n) => n.id !== notificacao.id));
    if (!notificacao.lida) {
      setNaoLidas((atual) => Math.max(0, atual - 1));
    }
    setIsNotificationsOpen(false);

    navigate(destinoDe(notificacao.tipo));

    if (notificacao.lida) return;
    try {
      await notificacoesService.marcarLida(notificacao.id);
    } catch {
      // Sem desfazer o estado otimista: na pior hipótese a próxima consulta ao
      // contador corrige o número, o que é menos ruidoso que reabrir o aviso.
    }
  };

  const marcarTodasComoLidas = async () => {
    setNotificacoes((atual) => atual.map((n) => ({ ...n, lida: true })));
    setNaoLidas(0);
    try {
      await notificacoesService.marcarTodasLidas();
    } catch {
      atualizarContador();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Implementar busca global
    console.log('Buscar por:', searchQuery);
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login');
  };

  return (
    <header className="bg-white shadow-sm border-b border-secondary-200 h-16 flex items-center justify-between px-4 lg:px-6">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Menu Toggle */}
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-md text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Nome da empresa */}
        {user.empresa && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary-50 rounded-lg">
            {/* A logo da empresa entra no lugar do ícone genérico. Sem logo
                cadastrada, o ícone continua valendo: nada fica quebrado. */}
            {user.logo
              ? <img src={user.logo} alt={`Logomarca da ${user.empresa}`} className="h-5 max-w-[88px] object-contain" />
              : <Building2 className="h-4 w-4 text-primary-600" />}
            <span className="text-sm font-medium text-primary-700">{user.empresa}</span>
          </div>
        )}

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="hidden md:flex items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar veículos, motoristas..."
              className="pl-10 pr-4 py-2 w-80 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        </form>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* Search Mobile */}
        <button className="p-2 rounded-md text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 md:hidden">
          <Search className="h-5 w-5" />
        </button>

        {/* Notifications */}
        <div className="relative">
          {/* O botao so tem um icone: sem rotulo, o leitor de tela anuncia
              apenas "botao". A contagem entra no nome porque o numero
              desenhado no badge tambem e so visual. */}
          <button
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            aria-label={naoLidas > 0 ? `Notificações, ${naoLidas} não lidas` : 'Notificações'}
            aria-expanded={isNotificationsOpen}
            className="relative p-2 rounded-md text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100"
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            {naoLidas > 0 && (
              <span
                data-testid="sininho-contador"
                className="absolute -top-1 -right-1 h-5 w-5 bg-danger-500 text-white text-xs rounded-full flex items-center justify-center"
              >
                {naoLidas > 99 ? '99+' : naoLidas}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {isNotificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-secondary-200 z-50">
              <div className="p-4 border-b border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900">Notificações</h3>
              </div>

              <div data-testid="lista-notificacoes" className="max-h-96 overflow-y-auto">
                {carregandoNotificacoes ? (
                  <div className="p-8 text-center text-secondary-500">Carregando...</div>
                ) : notificacoes.length > 0 ? (
                  notificacoes.map((notificacao) => (
                    <button
                      key={notificacao.id}
                      type="button"
                      onClick={() => abrirNotificacao(notificacao)}
                      className={`w-full text-left p-4 border-b border-secondary-100 hover:bg-secondary-50 ${
                        !notificacao.lida ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          !notificacao.lida ? 'bg-primary-500' : 'bg-secondary-300'}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-secondary-900">
                            {notificacao.titulo}
                          </p>
                          <p className="text-sm text-secondary-600 mt-1">
                            {notificacao.mensagem}
                          </p>
                          <p className="text-xs text-secondary-400 mt-1">
                            {formatRelativeTime(notificacao.created_at)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-8 text-center text-secondary-500">
                    <Bell className="h-8 w-8 mx-auto mb-2 text-secondary-300" />
                    <p>Nenhuma notificação</p>
                  </div>
                )}

                {/* Na última página o botão deixa de existir, em vez de ficar
                    desabilitado: nada a carregar, nada a mostrar. */}
                {paginacaoNotificacoes && paginacaoNotificacoes.current_page < paginacaoNotificacoes.last_page && (
                  <button
                    type="button"
                    onClick={carregarMaisNotificacoes}
                    disabled={carregandoMais}
                    className="w-full flex items-center justify-center gap-2 p-3 text-sm text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50 border-t border-secondary-100 disabled:opacity-60"
                  >
                    {carregandoMais ? 'Carregando mais...' : 'Carregar mais'}
                  </button>
                )}
              </div>

              {notificacoes.some((n) => !n.lida) && (
                <div className="p-3 border-t border-secondary-200">
                  <button
                    onClick={marcarTodasComoLidas}
                    className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Marcar todas como lidas
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center space-x-2 p-2 rounded-md text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100"
          >
            <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full" />
              ) : (
                <User className="h-4 w-4 text-white" />
              )}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-secondary-900">{user.name}</p>
              <p className="text-xs text-secondary-500">{user.role}</p>
            </div>
            <ChevronDown className="h-4 w-4" />
          </button>

          {/* User Dropdown */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-secondary-200 z-50">
              <div className="p-4 border-b border-secondary-200">
                <p className="text-sm font-medium text-secondary-900">{user.name}</p>
                <p className="text-sm text-secondary-500">{user.email}</p>
              </div>
              
              <div className="py-2">
                <button className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100">
                  <User className="h-4 w-4 mr-3" />
                  Meu Perfil
                </button>
                <button className="flex items-center w-full px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100">
                  <Settings className="h-4 w-4 mr-3" />
                  Configurações
                </button>
              </div>
              
              <div className="border-t border-secondary-200 py-2">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-danger-600 hover:bg-danger-50"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside handlers */}
      {(isUserMenuOpen || isNotificationsOpen) && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsUserMenuOpen(false);
            setIsNotificationsOpen(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;
